import React, { useState, useEffect } from 'react';
import './App.css';
import { CalendarIcon, SettingsIcon } from './assets/moduleIcons';
import { APP_VERSION } from './version';
import HelpCenter from './components/HelpCenter';
import LoginPage from './components/LoginPage';
import Sidebar from './components/Sidebar';
import ModulePage from './components/ModulePage';
import ProductsPage from './components/ProductsPage';
import TasksPage from './components/TasksPage';
import ProjectsPage from './components/ProjectsPage';
import AccountingReports from './components/AccountingReports';
import DocumentationPage from './components/DocumentationPage';
import SupportPage from './components/SupportPage';
import BugTrackerPage from './components/BugTrackerPage';
import SupportConsole from './components/SupportConsole';
import UserAccessPage from './components/UserAccessPage';
import { AccessProvider, useAccess } from './components/AccessContext';
import { FeaturesProvider } from './components/FeaturesContext';
import BugReportButton from './components/BugReportButton';
import Dashboard from './components/Dashboard';
import ChatPanel, { ChatToggleButton } from './components/ChatPanel';
import CalendarPanel from './components/CalendarPanel';
import { setBreadcrumb } from './services/breadcrumb';
import { ChatProvider } from './contexts/ChatContext';
import { CalendarProvider, useCalendarContext } from './contexts/CalendarContext';
import { SubscriptionProvider, useSubscriptionContext } from './contexts/SubscriptionContext';
import PaywallOverlay from './components/PaywallOverlay';
import api from './services/api';

// Calendar toggle button component
const CalendarToggleButton = () => {
  const { isPanelOpen, togglePanel } = useCalendarContext();

  return (
    <button
      className={`calendar-toggle-btn ${isPanelOpen ? 'active' : ''}`}
      onClick={togglePanel}
      title={isPanelOpen ? 'Close Calendar' : 'Open Calendar'}
    >
      {isPanelOpen ? '×' : <img src={CalendarIcon} alt="Calendar" className="module-png-icon" />}
    </button>
  );
};

// Subscription gated content wrapper - blurs content when paywall is active
const SubscriptionGatedContent = ({ children }) => {
  const { showPaywall } = useSubscriptionContext();
  return (
    <div className={showPaywall ? 'content-blurred' : ''}>
      {children}
    </div>
  );
};

// Account Settings Modal Component
// The text branding fields that carry per-field formatting (font/color/size/
// style). Labels drive the formatting-panel target readout.
const BRAND_FIELD_LABELS = {
  company_name: 'Company name', tagline: 'Tagline',
  address_line_1: 'Address line 1', address_line_2: 'Address line 2',
  website: 'Website', email: 'Email', phone: 'Phone',
};
const BRAND_FORMAT_FIELDS = Object.keys(BRAND_FIELD_LABELS);

// Map a TCPDF font key to a close web font so the field preview resembles the PDF.
const BRAND_FONT_CSS = {
  helvetica: 'Helvetica, Arial, sans-serif',
  times: 'Georgia, "Times New Roman", serif',
  courier: '"Courier New", monospace',
  dejavusans: '"DejaVu Sans", Verdana, sans-serif',
  dejavusansextralight: '"DejaVu Sans", Verdana, sans-serif',
  dejavusanscondensed: '"Arial Narrow", "DejaVu Sans Condensed", sans-serif',
  dejavuserif: '"DejaVu Serif", Georgia, serif',
  dejavuserifcondensed: '"DejaVu Serif Condensed", Georgia, serif',
  dejavusansmono: '"DejaVu Sans Mono", monospace',
  freesans: 'Helvetica, Arial, sans-serif',
  freeserif: 'Georgia, serif',
  freemono: 'monospace',
};
const brandFontCss = (k) => BRAND_FONT_CSS[k] || BRAND_FONT_CSS.helvetica;

// Inline preview style for a field's input, from its stored formatting. Color is
// NOT applied (it may be unreadable on the dark form); it shows as a swatch.
const brandFieldPreviewStyle = (branding, k) => {
  const style = branding[`${k}_style`] || '';
  const sizeRaw = parseFloat(branding[`${k}_size`]);
  const previewSize = isNaN(sizeRaw) ? 14 : Math.max(11, Math.min(22, sizeRaw));
  return {
    fontFamily: brandFontCss(branding[`${k}_font`] || 'helvetica'),
    fontWeight: style.includes('B') ? 700 : 400,
    fontStyle: style.includes('I') ? 'italic' : 'normal',
    textDecoration: style.includes('U') ? 'underline' : 'none',
    fontSize: `${previewSize}px`,
  };
};

// Branding tab helper: one text input that previews its formatting and, on focus,
// becomes the target of the shared formatting panel. Plain render fn (not a
// component) to avoid re-mount focus loss as `branding` updates per keystroke.
const renderBrandField = ({ k, label, placeholder, maxLength = 255, type = 'text', branding, setBranding, activeField, setActiveField }) => {
  const color = branding[`${k}_color`] || '';
  return (
    <div className="account-form-group" key={k}>
      <label>{label}</label>
      <div style={{ display: 'flex', gap: '0.45rem', alignItems: 'center' }}>
        <input
          type={type} maxLength={maxLength}
          value={branding[k] || ''}
          onChange={e => setBranding({ ...branding, [k]: e.target.value })}
          onFocus={() => setActiveField(k)}
          placeholder={placeholder}
          className={activeField === k ? 'brand-field-active' : ''}
          style={{ flex: 1, minWidth: 0, ...brandFieldPreviewStyle(branding, k) }}
        />
        <span
          title={color ? `Text color ${color}` : 'Default text color'}
          style={{
            width: 16, height: 16, borderRadius: 3, flexShrink: 0,
            border: '1px solid rgba(255,255,255,0.25)',
            background: color || 'repeating-linear-gradient(45deg,#555,#555 3px,#777 3px,#777 6px)',
          }}
        />
      </div>
    </div>
  );
};

// Shared "Text formatting" panel — edits the currently-focused field's font /
// color / size / style. Style is stored as a B/I/U combo, 'R' for explicit
// regular, '' for "use default". Disabled until a field is selected.
const renderBrandFormatPanel = ({ branding, setBranding, brandingFonts, activeField, accentHex }) => {
  const k = activeField;
  const disabled = !k;
  const style = (k && branding[`${k}_style`]) || '';
  const toggle = (ch) => {
    let next = ['B', 'I', 'U'].filter(c => (c === ch ? !style.includes(ch) : style.includes(c))).join('');
    if (next === '') next = 'R';
    setBranding({ ...branding, [`${k}_style`]: next });
  };
  const sizeVal = k ? (branding[`${k}_size`] || '') : '';
  const colorVal = k ? (branding[`${k}_color`] || '') : '';
  const fonts = brandingFonts.length ? brandingFonts : [{ key: 'helvetica', label: 'Helvetica' }];
  return (
    <div className={`brand-format-panel ${disabled ? 'disabled' : ''}`}>
      <div className="brand-format-title">
        Text formatting
        {k
          ? <span className="brand-format-target"> — {BRAND_FIELD_LABELS[k] || k}</span>
          : <span className="brand-format-hint"> — click a field below to format it</span>}
      </div>
      <div className="brand-format-controls">
        <label className="brand-format-ctl">Font
          <select disabled={disabled} value={k ? (branding[`${k}_font`] || 'helvetica') : 'helvetica'}
                  onChange={e => setBranding({ ...branding, [`${k}_font`]: e.target.value })}>
            {fonts.map(f => <option key={f.key} value={f.key}>{f.label}</option>)}
          </select>
        </label>
        <label className="brand-format-ctl">Color
          <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
            <input type="color" disabled={disabled} value={colorVal || accentHex || '#c2b280'}
                   onChange={e => setBranding({ ...branding, [`${k}_color`]: e.target.value.toUpperCase() })} />
            {!disabled && colorVal
              ? <button type="button" className="brand-format-clear" title="Use the default color"
                        onClick={() => setBranding({ ...branding, [`${k}_color`]: '' })}>auto</button>
              : <span className="brand-format-auto">auto</span>}
          </span>
        </label>
        <label className="brand-format-ctl">Size
          <input type="number" min="4" max="72" step="1" disabled={disabled} value={sizeVal} placeholder="auto"
                 onChange={e => setBranding({ ...branding, [`${k}_size`]: e.target.value })} style={{ width: 62 }} />
        </label>
        <div className="brand-format-style">
          <button type="button" disabled={disabled} className={style.includes('B') ? 'on' : ''} onClick={() => toggle('B')} style={{ fontWeight: 700 }}>B</button>
          <button type="button" disabled={disabled} className={style.includes('I') ? 'on' : ''} onClick={() => toggle('I')} style={{ fontStyle: 'italic' }}>I</button>
          <button type="button" disabled={disabled} className={style.includes('U') ? 'on' : ''} onClick={() => toggle('U')} style={{ textDecoration: 'underline' }}>U</button>
        </div>
      </div>
    </div>
  );
};

const AccountSettingsModal = ({ user, onClose, onUserUpdate }) => {
  const { subscription, refreshStatus } = useSubscriptionContext();
  const [activeTab, setActiveTab] = useState('profile');
  // Is this the control tenant (dev)? Gates the cross-tenant Support Console tab.
  const [isControlTenant, setIsControlTenant] = useState(false);
  const [devBusy, setDevBusy] = useState(false);
  const [devMsg, setDevMsg] = useState(null);
  // Branding tab — loaded on tab-open; one GET, then local state until Save.
  const [branding, setBranding] = useState(null);
  // Which branding text field the formatting panel currently targets.
  const [activeField, setActiveField] = useState(null);
  const [brandingLoading, setBrandingLoading] = useState(false);
  const [brandingSaving, setBrandingSaving] = useState(false);
  const [brandingMsg, setBrandingMsg] = useState(null);
  const [logoBust, setLogoBust] = useState(() => Date.now()); // cache-bust the preview
  // TCPDF font catalogue — one shot, cached across tab opens.
  const [brandingFonts, setBrandingFonts] = useState([]);
  // Features tab — per-tenant feature flags (admin-only). Loaded on tab-open.
  // Control tenant (dev) gets the assignment catalog; others get a toggle map.
  const [featuresMap, setFeaturesMap] = useState(null);         // non-control: runtime toggles
  const [featuresCatalog, setFeaturesCatalog] = useState(null); // control: {tenants, features}
  const [featuresLoading, setFeaturesLoading] = useState(false);
  const [featuresSaving, setFeaturesSaving] = useState('');
  const [featuresMsg, setFeaturesMsg] = useState(null);
  useEffect(() => {
    if (activeTab !== 'branding' || brandingFonts.length) return;
    fetch('/api/chat/branding.php?action=fonts')
      .then(r => r.json())
      .then(d => { if (d.success && Array.isArray(d.fonts)) setBrandingFonts(d.fonts); })
      .catch(() => { /* dropdown stays helvetica-only — safe fallback */ });
  }, [activeTab, brandingFonts.length]);
  // GET is public within the tenant (branding is on every outgoing PDF).
  // POSTs admin-gate on the user_id we pass — the shared Bearer apiuser
  // isn't admin, so a token-based gate would 403 every real admin.
  useEffect(() => {
    if (activeTab !== 'branding' || !user?.isAdmin) return;
    setBrandingLoading(true); setBrandingMsg(null);
    fetch('/api/chat/branding.php')
      .then(r => r.json().catch(() => ({ success: false, message: `HTTP ${r.status}` })))
      .then(d => {
        if (d.success) setBranding(d.branding);
        else { setBrandingMsg(d.message || 'Failed to load branding'); setBranding(null); }
      })
      .catch((e) => setBrandingMsg(e.message || 'Failed to load branding'))
      .finally(() => setBrandingLoading(false));
  }, [activeTab, user?.isAdmin]);
  // Features tab: on open, try the control catalog (dev only); otherwise load
  // this tenant's runtime toggle map.
  useEffect(() => {
    if (activeTab !== 'features' || !user?.isAdmin) return;
    let cancelled = false;
    (async () => {
      setFeaturesLoading(true); setFeaturesMsg(null);
      try {
        const cres = await fetch(`/api/chat/tenant-features.php?action=catalog&user_id=${encodeURIComponent(user.id)}`);
        const cdata = await cres.json().catch(() => ({}));
        if (!cancelled && cres.ok && cdata.success && cdata.is_control) {
          setFeaturesCatalog({ tenants: cdata.tenants || [], features: cdata.features || {} });
          setFeaturesMap(null);
        } else {
          const rres = await fetch('/api/chat/tenant-features.php');
          const rdata = await rres.json().catch(() => ({}));
          if (!cancelled) {
            if (rdata.success) { setFeaturesMap(rdata.features || {}); setFeaturesCatalog(null); }
            else setFeaturesMsg(rdata.message || 'Failed to load features');
          }
        }
      } catch (e) {
        if (!cancelled) setFeaturesMsg(e.message || 'Failed to load features');
      } finally {
        if (!cancelled) setFeaturesLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [activeTab, user?.isAdmin, user?.id]);

  // Detect the control tenant (dev) once per admin so the Support Console tab
  // only appears there. Reuses tenant-features' is_control signal (cheap).
  useEffect(() => {
    if (!user?.isAdmin) { setIsControlTenant(false); return; }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`/api/chat/tenant-features.php?action=catalog&user_id=${encodeURIComponent(user.id)}`);
        const d = await res.json().catch(() => ({}));
        if (!cancelled) setIsControlTenant(!!(res.ok && d.success && d.is_control));
      } catch (e) { if (!cancelled) setIsControlTenant(false); }
    })();
    return () => { cancelled = true; };
  }, [user?.isAdmin, user?.id]);

  const reloadFeaturesCatalog = async () => {
    const res = await fetch(`/api/chat/tenant-features.php?action=catalog&user_id=${encodeURIComponent(user.id)}`);
    const d = await res.json().catch(() => ({}));
    if (res.ok && d.success && d.is_control) setFeaturesCatalog({ tenants: d.tenants || [], features: d.features || {} });
  };

  // Non-control tenant: enable/disable one of its assigned flags.
  const toggleFeature = async (key, enabled) => {
    setFeaturesSaving(key); setFeaturesMsg(null);
    try {
      const res = await fetch('/api/chat/tenant-features.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: user?.id, key, enabled }),
      });
      const d = await res.json().catch(() => ({}));
      if (res.ok && d.success) { setFeaturesMap(d.features || {}); }
      else { setFeaturesMsg(d.message || 'Failed to save'); }
    } catch (e) {
      setFeaturesMsg(e.message || 'Failed to save');
    } finally {
      setFeaturesSaving('');
    }
  };

  // Control tenant (dev): enable/disable the flag for dev itself.
  const toggleFeatureDev = async (key, enabled) => {
    setFeaturesSaving(key); setFeaturesMsg(null);
    try {
      const res = await fetch('/api/chat/tenant-features.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: user?.id, key, enabled }),
      });
      const d = await res.json().catch(() => ({}));
      if (res.ok && d.success) { await reloadFeaturesCatalog(); }
      else { setFeaturesMsg(d.message || 'Failed to save'); }
    } catch (e) {
      setFeaturesMsg(e.message || 'Failed to save');
    } finally {
      setFeaturesSaving('');
    }
  };

  // Control tenant (dev): set which tenants a flag is assigned to ('*' = all).
  const assignFeature = async (key, tenants) => {
    setFeaturesSaving(key); setFeaturesMsg(null);
    try {
      const res = await fetch('/api/chat/tenant-features.php?action=assign', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: user?.id, key, tenants }),
      });
      const d = await res.json().catch(() => ({}));
      if (res.ok && d.success && d.is_control) setFeaturesCatalog({ tenants: d.tenants || [], features: d.features || {} });
      else setFeaturesMsg(d.message || 'Failed to save assignment');
    } catch (e) {
      setFeaturesMsg(e.message || 'Failed to save assignment');
    } finally {
      setFeaturesSaving('');
    }
  };

  const saveBranding = async () => {
    if (!branding) return;
    setBrandingSaving(true); setBrandingMsg(null);
    try {
      const body = {
        user_id: user?.id,
        company_name: branding.company_name || '',
        tagline: branding.tagline || '',
        primary_color_rgb: branding.primary_color_rgb || '',
        address_line_1: branding.address_line_1 || '',
        address_line_2: branding.address_line_2 || '',
        website: branding.website || '',
        email: branding.email || '',
        phone: branding.phone || '',
      };
      // Per-field formatting (font / color / size / style) from the panel.
      BRAND_FORMAT_FIELDS.forEach(f => {
        body[`${f}_font`] = branding[`${f}_font`] || 'helvetica';
        body[`${f}_color`] = branding[`${f}_color`] || '';
        body[`${f}_size`] = branding[`${f}_size`] || '';
        body[`${f}_style`] = branding[`${f}_style`] || '';
      });
      const r = await fetch('/api/chat/branding.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const d = await r.json();
      if (!d.success) throw new Error(d.message || 'Save failed');
      setBranding(d.branding);
      setBrandingMsg('Saved.');
      setTimeout(() => setBrandingMsg(null), 2000);
    } catch (e) {
      setBrandingMsg(e.message || 'Save failed');
    } finally { setBrandingSaving(false); }
  };
  const uploadLogo = async (file) => {
    if (!file) return;
    setBrandingSaving(true); setBrandingMsg(null);
    try {
      const fd = new FormData();
      fd.append('logo', file);
      if (user?.id) fd.append('user_id', user.id);
      const r = await fetch('/api/chat/branding.php', { method: 'POST', body: fd });
      const d = await r.json();
      if (!d.success) throw new Error(d.message || 'Upload failed');
      setBranding(d.branding);
      setLogoBust(Date.now());
      setBrandingMsg('Logo updated.');
      setTimeout(() => setBrandingMsg(null), 2000);
    } catch (e) {
      setBrandingMsg(e.message || 'Upload failed');
    } finally { setBrandingSaving(false); }
  };
  const removeLogo = async () => {
    if (!window.confirm('Remove the current logo? Documents will fall back to the company-name wordmark.')) return;
    setBrandingSaving(true); setBrandingMsg(null);
    try {
      const r = await fetch('/api/chat/branding.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: user?.id, clear_logo: true }),
      });
      const d = await r.json();
      if (!d.success) throw new Error(d.message || 'Remove failed');
      setBranding(d.branding);
      setLogoBust(Date.now());
    } catch (e) {
      setBrandingMsg(e.message || 'Remove failed');
    } finally { setBrandingSaving(false); }
  };
  // rgb "r,g,b" → "#RRGGBB"; '' when invalid, so the color input accepts a tweak.
  const rgbCsvToHex = (csv) => {
    if (!csv) return '#c2b280';
    const p = csv.split(',').map(x => parseInt(x, 10));
    if (p.length !== 3 || p.some(n => !Number.isFinite(n))) return '#c2b280';
    return '#' + p.map(n => Math.max(0, Math.min(255, n)).toString(16).padStart(2, '0')).join('');
  };
  const hexToRgbCsv = (hex) => {
    const h = (hex || '').replace('#', '');
    if (h.length !== 6) return '';
    const r = parseInt(h.slice(0, 2), 16), g = parseInt(h.slice(2, 4), 16), b = parseInt(h.slice(4, 6), 16);
    return [r, g, b].some(n => Number.isNaN(n)) ? '' : `${r},${g},${b}`;
  };

  // Developer-only helper: flip the tenant subscription so the paywall
  // fires (POST cancel) or clear it (POST activate/studio). Admin gate is
  // enforced by the tab visibility; the subscription endpoint itself is
  // shared with the real activation flow.
  const devTriggerPaywall = async () => {
    setDevBusy(true); setDevMsg(null);
    try {
      const r = await fetch('/api/chat/subscription.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'cancel' }),
      });
      const d = await r.json();
      if (!d.success) throw new Error(d.message || 'Cancel failed');
      await refreshStatus();
      setDevMsg('Paywall triggered — closing settings…');
      setTimeout(() => onClose(), 600);
    } catch (e) {
      setDevMsg(e.message || 'Failed to trigger paywall');
    }
    setDevBusy(false);
  };

  const devRestoreSubscription = async () => {
    setDevBusy(true); setDevMsg(null);
    try {
      const r = await fetch('/api/chat/subscription.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'activate', plan: 'studio', billing: 'annual', user_id: user?.id }),
      });
      const d = await r.json();
      if (!d.success) throw new Error(d.message || 'Activate failed');
      await refreshStatus();
      setDevMsg('Subscription restored to Studio (annual).');
    } catch (e) {
      setDevMsg(e.message || 'Failed to restore subscription');
    }
    setDevBusy(false);
  };

  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Profile form state
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    title: '',
    department: '',
    companyName: '',
    phoneWork: '',
    phoneMobile: ''
  });

  // Password form state
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  // Stripe connection state (loaded when Payments tab opens)
  const [stripeStatus, setStripeStatus] = useState(null);
  const [stripeLoading, setStripeLoading] = useState(false);
  const [atelierBilling, setAtelierBilling] = useState(null);
  const [atelierBillingLoading, setAtelierBillingLoading] = useState(false);
  useEffect(() => {
    if (activeTab !== 'payments' || !user?.isAdmin) return;
    setStripeLoading(true);
    setAtelierBillingLoading(true);
    fetch('/api/chat/stripe-status.php')
      .then(r => r.json()).then(d => setStripeStatus(d))
      .catch(() => setStripeStatus({ success: false, message: 'Failed to load' }))
      .finally(() => setStripeLoading(false));
    fetch('/api/chat/atelier-billing-status.php')
      .then(r => r.json()).then(d => setAtelierBilling(d))
      .catch(() => setAtelierBilling({ success: false, message: 'Failed to load' }))
      .finally(() => setAtelierBillingLoading(false));
  }, [activeTab, user?.isAdmin]);

  const handleStripeConnect = () => {
    // Full-page nav — Stripe's OAuth flow needs top-frame context.
    window.location.href = `/api/chat/stripe-oauth-start.php?user_id=${encodeURIComponent(user.id)}`;
  };

  const handleAtelierSubscribe = () => {
    window.location.href = `/api/chat/atelier-billing-start.php?user_id=${encodeURIComponent(user.id)}`;
  };

  const handleAtelierManage = (flow) => {
    // Full-page nav — Stripe portal is a hosted URL that expects top-frame context.
    // `flow` is optional; 'update_card' deep-links straight to the add-payment-method
    // panel, used from the In-Grace state to save the tenant clicks.
    const flowParam = flow ? `&flow=${encodeURIComponent(flow)}` : '';
    window.location.href = `/api/chat/atelier-billing-portal.php?user_id=${encodeURIComponent(user.id)}${flowParam}`;
  };

  const handleStripeDisconnect = async () => {
    if (!window.confirm('Disconnect Stripe? Payment collection will stop working until reconnected.')) return;
    try {
      const r = await fetch(`/api/chat/stripe-status.php?user_id=${encodeURIComponent(user.id)}`, { method: 'DELETE' });
      const d = await r.json();
      if (d.success) {
        setStripeStatus({ success: true, connected: false, platform_ready: stripeStatus?.platform_ready });
      } else {
        alert(d.message || 'Disconnect failed');
      }
    } catch (e) {
      alert('Disconnect failed: ' + e.message);
    }
  };

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const response = await fetch(`/api/chat/profile.php?user_id=${user.id}`);
        const data = await response.json();
        if (data.success) {
          setProfile(data.profile);
          setFormData({
            firstName: data.profile.firstName || '',
            lastName: data.profile.lastName || '',
            email: data.profile.email || '',
            title: data.profile.title || '',
            department: data.profile.department || '',
            companyName: data.profile.companyName || '',
            phoneWork: data.profile.phoneWork || '',
            phoneMobile: data.profile.phoneMobile || ''
          });
        }
      } catch (err) {
        setError('Failed to load profile');
      }
      setLoading(false);
    };
    fetchProfile();
  }, [user.id]);

  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({ ...prev, [name]: value }));
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await fetch('/api/chat/profile.php', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: user.id, ...formData })
      });
      const data = await response.json();

      if (data.success) {
        setSuccess('Profile updated successfully');
        // Update session storage with new name
        const updatedUser = {
          ...user,
          fullName: `${formData.firstName} ${formData.lastName}`.trim() || user.username
        };
        sessionStorage.setItem('user', JSON.stringify(updatedUser));
        onUserUpdate(updatedUser);
      } else {
        setError(data.message || 'Failed to update profile');
      }
    } catch (err) {
      setError('Failed to update profile');
    }
    setSaving(false);
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setError('New passwords do not match');
      return;
    }

    if (passwordData.newPassword.length < 6) {
      setError('New password must be at least 6 characters');
      return;
    }

    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await fetch('/api/chat/profile.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: user.id,
          currentPassword: passwordData.currentPassword,
          newPassword: passwordData.newPassword
        })
      });
      const data = await response.json();

      if (data.success) {
        setSuccess('Password changed successfully');
        setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      } else {
        setError(data.message || 'Failed to change password');
      }
    } catch (err) {
      setError('Failed to change password');
    }
    setSaving(false);
  };

  return (
    <div className="account-settings-overlay" onClick={onClose}>
      <div className="account-settings-modal" onClick={(e) => e.stopPropagation()}>
        <div className="account-settings-header">
          <h2>Account Settings</h2>
          <button className="account-settings-close" onClick={onClose}>×</button>
        </div>

        <div className="account-settings-tabs">
          <button
            className={`account-tab ${activeTab === 'profile' ? 'active' : ''}`}
            onClick={() => setActiveTab('profile')}
          >
            Profile
          </button>
          <button
            className={`account-tab ${activeTab === 'password' ? 'active' : ''}`}
            onClick={() => setActiveTab('password')}
          >
            Change Password
          </button>
          {user?.isAdmin && (
            <button
              className={`account-tab ${activeTab === 'payments' ? 'active' : ''}`}
              onClick={() => setActiveTab('payments')}
            >
              Payments
            </button>
          )}
          {user?.isAdmin && (
            <button
              className={`account-tab ${activeTab === 'branding' ? 'active' : ''}`}
              onClick={() => setActiveTab('branding')}
              title="Logo, accent color, company name used on generated PDFs"
            >
              Branding
            </button>
          )}
          {user?.isAdmin && (
            <button
              className={`account-tab ${activeTab === 'developer' ? 'active' : ''}`}
              onClick={() => setActiveTab('developer')}
              title="Developer utilities"
            >
              Developer
            </button>
          )}
          {user?.isAdmin && (
            <button
              className={`account-tab ${activeTab === 'features' ? 'active' : ''}`}
              onClick={() => setActiveTab('features')}
              title="Per-tenant feature flags"
            >
              Features
            </button>
          )}
          {user?.isAdmin && isControlTenant && (
            <button
              className={`account-tab ${activeTab === 'support' ? 'active' : ''}`}
              onClick={() => setActiveTab('support')}
              title="Cross-tenant support console"
            >
              Support Console
            </button>
          )}
        </div>

        <div className="account-settings-content">
          {loading ? (
            <div className="account-loading">Loading...</div>
          ) : (
            <>
              {error && <div className="account-error">{error}</div>}
              {success && <div className="account-success">{success}</div>}

              {activeTab === 'profile' && (
                <form onSubmit={handleProfileSubmit} className="account-form">
                  <div className="account-form-row">
                    <div className="account-form-group">
                      <label>First Name</label>
                      <input
                        type="text"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleProfileChange}
                        placeholder="First name"
                      />
                    </div>
                    <div className="account-form-group">
                      <label>Last Name</label>
                      <input
                        type="text"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleProfileChange}
                        placeholder="Last name"
                      />
                    </div>
                  </div>

                  <div className="account-form-group">
                    <label>Email</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleProfileChange}
                      placeholder="Email address"
                    />
                  </div>

                  <div className="account-form-row">
                    <div className="account-form-group">
                      <label>Title</label>
                      <input
                        type="text"
                        name="title"
                        value={formData.title}
                        onChange={handleProfileChange}
                        placeholder="Job title"
                      />
                    </div>
                    <div className="account-form-group">
                      <label>Department</label>
                      <input
                        type="text"
                        name="department"
                        value={formData.department}
                        onChange={handleProfileChange}
                        placeholder="Department"
                      />
                    </div>
                  </div>

                  <div className="account-form-row">
                    <div className="account-form-group">
                      <label>Company Name</label>
                      <input
                        type="text"
                        name="companyName"
                        value={formData.companyName}
                        onChange={handleProfileChange}
                        placeholder="Company name"
                      />
                    </div>
                  </div>

                  <div className="account-form-row">
                    <div className="account-form-group">
                      <label>Work Phone</label>
                      <input
                        type="tel"
                        name="phoneWork"
                        value={formData.phoneWork}
                        onChange={handleProfileChange}
                        placeholder="Work phone"
                      />
                    </div>
                    <div className="account-form-group">
                      <label>Mobile Phone</label>
                      <input
                        type="tel"
                        name="phoneMobile"
                        value={formData.phoneMobile}
                        onChange={handleProfileChange}
                        placeholder="Mobile phone"
                      />
                    </div>
                  </div>

                  <div className="account-form-actions">
                    <button type="submit" className="account-save-btn" disabled={saving}>
                      {saving ? 'Saving...' : 'Save Profile'}
                    </button>
                  </div>
                </form>
              )}

              {activeTab === 'password' && (
                <form onSubmit={handlePasswordSubmit} className="account-form">
                  <div className="account-form-group">
                    <label>Current Password</label>
                    <input
                      type="password"
                      name="currentPassword"
                      value={passwordData.currentPassword}
                      onChange={handlePasswordChange}
                      placeholder="Enter current password"
                      required
                    />
                  </div>

                  <div className="account-form-group">
                    <label>New Password</label>
                    <input
                      type="password"
                      name="newPassword"
                      value={passwordData.newPassword}
                      onChange={handlePasswordChange}
                      placeholder="Enter new password (min 6 characters)"
                      required
                    />
                  </div>

                  <div className="account-form-group">
                    <label>Confirm New Password</label>
                    <input
                      type="password"
                      name="confirmPassword"
                      value={passwordData.confirmPassword}
                      onChange={handlePasswordChange}
                      placeholder="Confirm new password"
                      required
                    />
                  </div>

                  <div className="account-form-actions">
                    <button type="submit" className="account-save-btn" disabled={saving}>
                      {saving ? 'Changing...' : 'Change Password'}
                    </button>
                  </div>
                </form>
              )}

              {activeTab === 'payments' && user?.isAdmin && (
                <div className="account-form">
                  <h3 style={{ marginTop: 0 }}>Stripe (customer payments)</h3>
                  {stripeLoading && <div>Loading connection status…</div>}
                  {!stripeLoading && stripeStatus && stripeStatus.platform_ready === false && (
                    <div className="account-error">
                      Stripe platform credentials are not configured on this server. Ask the operator to set them before connecting an account.
                    </div>
                  )}
                  {!stripeLoading && stripeStatus?.platform_ready && !stripeStatus.connected && (
                    <>
                      <p style={{ color: '#555' }}>
                        Connect your Stripe account so you can accept credit-card and bank-transfer payments on your invoices, proposal deposits, and retainers. You'll be redirected to Stripe to authorize the connection.
                      </p>
                      <div className="account-form-actions">
                        <button type="button" className="account-save-btn" onClick={handleStripeConnect}>
                          Connect Stripe
                        </button>
                      </div>
                    </>
                  )}
                  {!stripeLoading && stripeStatus?.connected && (
                    <>
                      <p style={{ color: '#555' }}>
                        Connected to Stripe account <strong style={{ fontFamily: 'monospace' }}>{stripeStatus.stripe_user_id}</strong>
                        {stripeStatus.livemode ? ' (live mode)' : ' (test mode)'}.
                      </p>
                      <p style={{ color: '#888', fontSize: '0.85rem' }}>
                        Connected {stripeStatus.connected_at ? new Date(stripeStatus.connected_at).toLocaleString() : ''}
                      </p>
                      <div className="account-form-actions">
                        <button type="button" className="account-save-btn" style={{ background: '#b3261e' }} onClick={handleStripeDisconnect}>
                          Disconnect Stripe
                        </button>
                      </div>
                    </>
                  )}

                  <hr style={{ margin: '2rem 0', border: 0, borderTop: '1px solid rgba(194, 178, 128, 0.25)' }} />

                  <h3>Atelier Studio subscription</h3>
                  {atelierBillingLoading && <div>Loading subscription…</div>}
                  {!atelierBillingLoading && atelierBilling && atelierBilling.price_configured === false && (
                    <div className="account-error">
                      Subscription pricing isn't configured on this server yet. Ask the operator to set STRIPE_ATELIER_PRICE_ID.
                    </div>
                  )}
                  {!atelierBillingLoading && atelierBilling?.state === 'no_subscription' && atelierBilling.price_configured && (
                    <>
                      <p style={{ color: '#555' }}>
                        Your Atelier Studio subscription is <strong>${atelierBilling.monthly_price_usd}/month</strong>. Payment is handled securely by Stripe.
                      </p>
                      <div className="account-form-actions">
                        <button type="button" className="account-save-btn" onClick={handleAtelierSubscribe}>
                          Start Subscription
                        </button>
                      </div>
                    </>
                  )}
                  {!atelierBillingLoading && atelierBilling?.state === 'active' && (
                    <>
                      <p style={{ color: '#57c85a', fontWeight: 700 }}>Active</p>
                      <p style={{ color: '#555', fontSize: '0.9rem' }}>
                        Next renewal: {atelierBilling.current_period_end ? new Date(atelierBilling.current_period_end).toLocaleDateString() : '—'}
                      </p>
                      <div className="account-form-actions">
                        <button type="button" className="account-save-btn" onClick={() => handleAtelierManage()}>
                          Manage Subscription
                        </button>
                      </div>
                      <p style={{ color: '#888', fontSize: '0.8rem', marginTop: '6px' }}>
                        Opens Stripe to update your card, view invoices, or cancel.
                      </p>
                    </>
                  )}
                  {!atelierBillingLoading && atelierBilling?.state === 'in_grace' && (
                    <>
                      <p style={{ color: '#e0a742', fontWeight: 700 }}>Payment past due — grace period</p>
                      <p style={{ color: '#555', fontSize: '0.9rem' }}>
                        Update your payment method within <strong>{atelierBilling.grace_days_remaining} day{atelierBilling.grace_days_remaining === 1 ? '' : 's'}</strong> to avoid suspension. Stripe is retrying the charge automatically.
                      </p>
                      <div className="account-form-actions">
                        <button type="button" className="account-save-btn" onClick={() => handleAtelierManage('update_card')}>
                          Update Payment Method
                        </button>
                      </div>
                    </>
                  )}
                  {!atelierBillingLoading && atelierBilling?.state === 'suspended' && (
                    <>
                      <p style={{ color: '#b3261e', fontWeight: 700 }}>Suspended</p>
                      <p style={{ color: '#555', fontSize: '0.9rem' }}>
                        Your subscription is canceled or unpaid. Restart to continue using the platform.
                      </p>
                      <div className="account-form-actions">
                        <button type="button" className="account-save-btn" onClick={handleAtelierSubscribe}>
                          Restart Subscription
                        </button>
                      </div>
                    </>
                  )}
                </div>
              )}

              {activeTab === 'branding' && user?.isAdmin && (
                // Edits the per-tenant branding row that drives generate-pdf.php:
                // company name wordmark / logo image + accent color used on
                // every outgoing invoice, proposal and PO.
                <div className="account-form">
                  {brandingLoading ? (
                    <div className="account-loading">Loading branding…</div>
                  ) : !branding ? (
                    <div className="account-error">{brandingMsg || 'Failed to load branding.'}</div>
                  ) : (
                    <>
                      {renderBrandFormatPanel({ branding, setBranding, brandingFonts, activeField,
                                                accentHex: rgbCsvToHex(branding.primary_color_rgb) })}

                      <div className="account-form-row">
                        {renderBrandField({ k: 'company_name', label: 'Company name', placeholder: 'Atelier Studio',
                                            branding, setBranding, activeField, setActiveField })}
                        {renderBrandField({ k: 'tagline', label: 'Tagline', placeholder: 'Interior Design & Furnishings',
                                            branding, setBranding, activeField, setActiveField })}
                      </div>

                      <div className="account-form-group">
                        <label>Logo</label>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
                          <div
                            style={{
                              width: 160, height: 80, borderRadius: 6,
                              background: 'rgba(0,0,0,0.3)',
                              border: '1px solid rgba(194,178,128,0.3)',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              padding: 8, overflow: 'hidden',
                            }}
                          >
                            {branding.has_logo ? (
                              <img
                                src={`/api/chat/branding.php?action=logo&v=${logoBust}`}
                                alt="Logo"
                                style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                              />
                            ) : (
                              <span style={{ color: '#777', fontSize: '0.8rem' }}>No logo</span>
                            )}
                          </div>
                          <div style={{ display: 'flex', gap: '0.5rem', flexDirection: 'column' }}>
                            <label className="account-save-btn" style={{ cursor: 'pointer', textAlign: 'center' }}>
                              {brandingSaving ? 'Uploading…' : 'Upload logo'}
                              <input
                                type="file"
                                accept="image/png,image/jpeg,application/pdf"
                                style={{ display: 'none' }}
                                disabled={brandingSaving}
                                onChange={e => { const f = e.target.files?.[0]; if (f) uploadLogo(f); e.target.value = ''; }}
                              />
                            </label>
                            {branding.has_logo && (
                              <button type="button" className="account-save-btn" onClick={removeLogo} disabled={brandingSaving}
                                      style={{ background: 'transparent', borderColor: 'rgba(220,53,69,0.5)', color: '#e57373' }}>
                                Remove
                              </button>
                            )}
                          </div>
                          <div style={{ color: '#b0a894', fontSize: '0.8rem', flex: 1, minWidth: 180 }}>
                            PNG, JPG, or PDF. Uploaded files are resized to 800px wide.
                            Uploading a new logo also refreshes the accent color below.
                          </div>
                        </div>
                      </div>

                      <div className="account-form-row">
                        <div className="account-form-group">
                          <label>Accent color</label>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <input
                              type="color"
                              value={rgbCsvToHex(branding.primary_color_rgb)}
                              onChange={e => setBranding({ ...branding, primary_color_rgb: hexToRgbCsv(e.target.value) })}
                              style={{ width: 44, height: 36, padding: 0, border: 'none', background: 'transparent', cursor: 'pointer' }}
                            />
                            <input
                              type="text"
                              value={rgbCsvToHex(branding.primary_color_rgb).toUpperCase()}
                              onChange={e => {
                                const v = e.target.value.trim();
                                if (/^#?[0-9A-Fa-f]{6}$/.test(v)) {
                                  setBranding({ ...branding, primary_color_rgb: hexToRgbCsv(v.startsWith('#') ? v : '#' + v) });
                                }
                              }}
                              maxLength={7}
                              style={{ fontFamily: 'monospace', width: 110 }}
                            />
                          </div>
                        </div>
                        {renderBrandField({ k: 'website', label: 'Website', placeholder: 'atelierstudio.us',
                                            branding, setBranding, activeField, setActiveField })}
                      </div>

                      <div className="account-form-row">
                        {renderBrandField({ k: 'address_line_1', label: 'Address line 1',
                                            branding, setBranding, activeField, setActiveField })}
                        {renderBrandField({ k: 'address_line_2', label: 'Address line 2',
                                            branding, setBranding, activeField, setActiveField })}
                      </div>

                      <div className="account-form-row">
                        {renderBrandField({ k: 'email', label: 'Email', type: 'email',
                                            branding, setBranding, activeField, setActiveField })}
                        {renderBrandField({ k: 'phone', label: 'Phone', maxLength: 100,
                                            branding, setBranding, activeField, setActiveField })}
                      </div>

                      <div className="account-form-actions">
                        <span style={{ marginRight: 'auto', color: '#b0a894', fontSize: '0.8rem' }}>
                          {brandingMsg || (branding.source_pdf_name ? `Last extracted from: ${branding.source_pdf_name}` : '')}
                        </span>
                        <button type="button" className="account-save-btn" onClick={saveBranding} disabled={brandingSaving}>
                          {brandingSaving ? 'Saving…' : 'Save branding'}
                        </button>
                      </div>
                    </>
                  )}
                </div>
              )}

              {activeTab === 'developer' && user?.isAdmin && (
                // Development utilities — quick way to preview the paywall
                // without waiting for a subscription to actually expire.
                <div className="account-form">
                  <div className="account-form-group">
                    <label>Current subscription</label>
                    <div style={{ fontSize: '0.9rem', color: '#c5c5c5' }}>
                      Plan: <strong style={{ color: '#e8e2d0' }}>{subscription?.plan_type || '—'}</strong>
                      {' · '}Status: <strong style={{ color: subscription?.is_expired ? '#e57373' : '#6bcf70' }}>
                        {subscription?.is_expired ? 'expired' : (subscription?.status || '—')}
                      </strong>
                      {subscription?.days_remaining !== undefined && !subscription?.is_expired && (
                        <> · {subscription.days_remaining} days remaining</>
                      )}
                    </div>
                  </div>

                  <div className="account-form-group">
                    <label>Paywall preview</label>
                    <p style={{ color: '#b0a894', fontSize: '0.85rem', margin: '0 0 0.75rem' }}>
                      Cancel the tenant's subscription so the paywall renders on the
                      next status check. Use "Restore" to reactivate on the Studio plan.
                      Developer-only — do not use on a real customer tenant.
                    </p>
                    <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                      <button
                        type="button"
                        className="account-save-btn"
                        style={{ background: 'rgba(229, 115, 115, 0.15)', borderColor: '#e57373', color: '#f2c0bd' }}
                        onClick={devTriggerPaywall}
                        disabled={devBusy}
                      >
                        {devBusy ? 'Working…' : '🔒 Trigger Paywall'}
                      </button>
                      <button
                        type="button"
                        className="account-save-btn"
                        onClick={devRestoreSubscription}
                        disabled={devBusy}
                      >
                        {devBusy ? 'Working…' : 'Restore Subscription (Studio)'}
                      </button>
                    </div>
                  </div>

                  {devMsg && (
                    <div style={{ marginTop: '0.5rem', color: '#c2b280', fontSize: '0.85rem' }}>
                      {devMsg}
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'support' && user?.isAdmin && isControlTenant && (
                <div className="account-form">
                  <SupportConsole user={user} />
                </div>
              )}

              {activeTab === 'features' && user?.isAdmin && (
                <div className="account-form">
                  {featuresLoading && <div style={{ color: '#c5c5c5' }}>Loading…</div>}

                  {/* Control tenant (dev): assign each feature to tenants. */}
                  {!featuresLoading && featuresCatalog && (
                    <>
                      <p style={{ color: '#b0a894', fontSize: '0.85rem', margin: '0 0 1rem' }}>
                        Assign each feature to the tenants that should see it. Tenants see only
                        the features assigned to them; assignments propagate within a minute or two.
                      </p>
                      {Object.keys(featuresCatalog.features).length === 0 && (
                        <div style={{ color: '#b0a894', fontSize: '0.9rem' }}>No feature flags are defined yet.</div>
                      )}
                      {Object.entries(featuresCatalog.features).map(([key, f]) => {
                        const assigned = Array.isArray(f.tenants) ? f.tenants : [];
                        const isAll = assigned.includes('*');
                        const devAssigned = isAll || assigned.includes('dev');
                        const setTenant = (t, on) => {
                          const next = new Set(assigned.filter(x => x !== '*'));
                          if (on) next.add(t); else next.delete(t);
                          assignFeature(key, Array.from(next));
                        };
                        return (
                          <div key={key} className="account-form-group"
                               style={{ borderTop: '1px solid rgba(194,178,128,0.15)', paddingTop: '0.85rem' }}>
                            <div style={{ color: '#e8e2d0', fontWeight: 600 }}>
                              {f.label || key}
                              {featuresSaving === key && (
                                <span style={{ color: '#c2b280', fontWeight: 400, fontSize: '0.8rem' }}> · saving…</span>
                              )}
                            </div>
                            {f.description && (
                              <div style={{ color: '#b0a894', fontSize: '0.82rem', marginTop: '0.2rem' }}>{f.description}</div>
                            )}
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem 1rem', marginTop: '0.55rem', alignItems: 'center' }}>
                              <span style={{ color: '#7d7462', fontSize: '0.78rem' }}>Available to:</span>
                              <label style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem', cursor: 'pointer', color: '#d8c89a', fontSize: '0.82rem' }}>
                                <input type="checkbox" checked={isAll} disabled={featuresSaving === key}
                                       onChange={(e) => assignFeature(key, e.target.checked ? ['*'] : [])} />
                                All tenants
                              </label>
                              {(featuresCatalog.tenants || []).map(t => (
                                <label key={t} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem', cursor: isAll ? 'default' : 'pointer', color: '#c5c5c5', fontSize: '0.82rem', opacity: isAll ? 0.5 : 1 }}>
                                  <input type="checkbox" checked={isAll || assigned.includes(t)} disabled={isAll || featuresSaving === key}
                                         onChange={(e) => setTenant(t, e.target.checked)} />
                                  {t}
                                </label>
                              ))}
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginTop: '0.5rem' }}>
                              <input type="checkbox" checked={!!f.enabled} disabled={!devAssigned || featuresSaving === key}
                                     onChange={(e) => toggleFeatureDev(key, e.target.checked)} />
                              <span style={{ color: devAssigned ? '#c5c5c5' : '#7d7462', fontSize: '0.8rem' }}>
                                Enabled on dev{!devAssigned ? ' (assign dev first)' : ''}
                              </span>
                              <span style={{ color: '#7d7462', fontSize: '0.72rem', marginLeft: 'auto' }}>
                                key: <code>{key}</code>
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </>
                  )}

                  {/* Non-control tenant: enable/disable the features assigned to it. */}
                  {!featuresLoading && featuresMap && (
                    <>
                      <p style={{ color: '#b0a894', fontSize: '0.85rem', margin: '0 0 1rem' }}>
                        Turn your available features on or off. Changes apply immediately and only affect this tenant.
                      </p>
                      {Object.keys(featuresMap).length === 0 && (
                        <div style={{ color: '#b0a894', fontSize: '0.9rem' }}>No features are available to this tenant yet.</div>
                      )}
                      {Object.entries(featuresMap).map(([key, f]) => (
                        <div key={key} className="account-form-group"
                             style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem',
                                      borderTop: '1px solid rgba(194,178,128,0.15)', paddingTop: '0.85rem' }}>
                          <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', marginTop: '0.15rem' }}>
                            <input type="checkbox" checked={!!f.enabled} disabled={featuresSaving === key}
                                   onChange={(e) => toggleFeature(key, e.target.checked)} />
                          </label>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ color: '#e8e2d0', fontWeight: 600 }}>
                              {f.label || key}
                              {featuresSaving === key && (
                                <span style={{ color: '#c2b280', fontWeight: 400, fontSize: '0.8rem' }}> · saving…</span>
                              )}
                            </div>
                            {f.description && (
                              <div style={{ color: '#b0a894', fontSize: '0.82rem', marginTop: '0.2rem' }}>{f.description}</div>
                            )}
                          </div>
                        </div>
                      ))}
                    </>
                  )}

                  {featuresMsg && (
                    <div style={{ marginTop: '0.75rem', color: '#e57373', fontSize: '0.85rem' }}>{featuresMsg}</div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// User Menu Dropdown Component
const UserMenu = ({ user, onLogout, onOpenSettings, onOpenUserAccess }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="user-menu">
      <button
        className="user-menu-trigger"
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className="user-avatar">👤</span>
        <span className="user-name">{user.fullName || user.username}</span>
        <span className="user-menu-arrow">{isOpen ? '▲' : '▼'}</span>
      </button>

      {isOpen && (
        <>
          <div className="user-menu-backdrop" onClick={() => setIsOpen(false)} />
          <div className="user-menu-dropdown">
            <button
              className="user-menu-item"
              onClick={() => {
                setIsOpen(false);
                onOpenSettings();
              }}
            >
              <span className="menu-icon"><img src={SettingsIcon} alt="" className="module-png-icon" /></span>
              Account Settings
            </button>
            {user.isAdmin && (
              <button
                className="user-menu-item"
                onClick={() => {
                  setIsOpen(false);
                  onOpenUserAccess();
                }}
              >
                <span className="menu-icon">🔐</span>
                User Access
              </button>
            )}
            {user.isAdmin && (
              <a
                className="user-menu-item"
                href="/index.php"
                onClick={() => setIsOpen(false)}
              >
                <span className="menu-icon">🛠️</span>
                Access SuiteCRM
              </a>
            )}
            <div className="user-menu-divider" />
            <button
              className="user-menu-item logout"
              onClick={() => {
                setIsOpen(false);
                onLogout();
              }}
            >
              <span className="menu-icon">🚪</span>
              Logout
            </button>
          </div>
        </>
      )}
    </div>
  );
};

// Help Menu Dropdown Component
const HelpMenu = ({ onSelectHelpCenter, onSelectAbout }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="help-menu">
      <button
        className="help-menu-trigger"
        onClick={() => setIsOpen(!isOpen)}
        title="Help"
      >
        ?
      </button>

      {isOpen && (
        <>
          <div className="help-menu-backdrop" onClick={() => setIsOpen(false)} />
          <div className="help-menu-dropdown">
            <button
              className="help-menu-item"
              onClick={() => { setIsOpen(false); onSelectHelpCenter(); }}
            >
              <span className="menu-icon">❓</span>
              Help Center
            </button>
            <div className="help-menu-divider" />
            <button
              className="help-menu-item"
              onClick={() => { setIsOpen(false); onSelectAbout(); }}
            >
              <span className="menu-icon">ℹ️</span>
              About
            </button>
          </div>
        </>
      )}
    </div>
  );
};

// About Modal Component
const AboutModal = ({ onClose }) => {
  return (
    <div className="about-modal-overlay" onClick={onClose}>
      <div className="about-modal" onClick={(e) => e.stopPropagation()}>
        <div className="about-modal-header">
          <h2>About Atelier Studio</h2>
          <button className="account-settings-close" onClick={onClose}>×</button>
        </div>
        <div className="about-modal-content">
          <div className="about-logo">🏛️</div>
          <h3 className="about-app-name">Atelier Studio</h3>
          <p className="about-version">Version {APP_VERSION}</p>
          <p className="about-tagline">Interior Design Excellence</p>
          <div className="about-divider" />
          <p className="about-description">
            A sophisticated CRM solution tailored for interior designers.
            Manage clients, projects, and portfolios with elegance and efficiency.
          </p>
          <div className="about-divider" />
          <div className="about-credits">
            <p>Powered by SuiteCRM 7.14.7 &middot; API v8</p>
            <p>&copy; 2026 Atelier Studio. All rights reserved.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Shown when a user opens a page they're not allowed to access.
const NoAccessPanel = ({ onBack }) => (
  <div className="no-access-panel">
    <div className="no-access-card">
      <div className="no-access-icon">🔒</div>
      <h2>You don’t have access to this page</h2>
      <p>Your administrator has restricted access to this area. If you think this is a mistake, please contact your administrator.</p>
      <button className="no-access-btn" onClick={onBack}>Back to Dashboard</button>
    </div>
  </div>
);

// Main app content component (needs to be inside providers)
const AppContent = ({ user, onLogout, onUserUpdate }) => {
  const access = useAccess();
  const [currentModule, setCurrentModule] = useState(null);
  const [showAccountSettings, setShowAccountSettings] = useState(false);
  const [showAboutModal, setShowAboutModal] = useState(false);
  const [showHelp, setShowHelp] = useState(false);

  // A dashboard card can request opening a specific record: we set the target
  // module and stash the record id, which the module page consumes on mount.
  const [pendingRecord, setPendingRecord] = useState(null); // { module, id }

  const handleModuleSelect = (moduleName) => {
    setPendingRecord(null);
    setCurrentModule(moduleName);
  };

  const openRecord = (moduleName, id) => {
    if (!moduleName || !id) return;
    setPendingRecord({ module: moduleName, id });
    setCurrentModule(moduleName);
  };
  const consumePendingRecord = () => setPendingRecord(null);

  // Track the top-level location for bug reports. Pages that manage their own
  // (deeper) breadcrumb set it themselves: Projects (project → room → tab) and
  // every generic module rendered by ModulePage (module → record). App only
  // owns the pages it renders directly here.
  useEffect(() => {
    if (!currentModule) { setBreadcrumb(['Dashboard']); return; }
    const appOwned = {
      AOS_Products: 'Products', jcrm_Accounting: 'Accounting',
      __documentation: 'Documentation', __support: 'Support', __bugtracker: 'Bug Tracker',
      __user_access: 'User Access',
    };
    if (appOwned[currentModule]) setBreadcrumb([appOwned[currentModule]]);
    // Project and ModulePage-rendered modules push their own breadcrumb.
  }, [currentModule]);

  const handleBackToDashboard = () => {
    setCurrentModule(null);
  };

  // Show module page if a module is selected
  if (currentModule) {
    // Use specialized pages for specific modules
    const isProductsModule = currentModule === 'AOS_Products';
    const isProjectsModule = currentModule === 'Project';

    const renderModuleContent = () => {
      // Admin-only User Access screen.
      if (currentModule === '__user_access') {
        if (!user?.isAdmin) return <NoAccessPanel onBack={handleBackToDashboard} />;
        return <UserAccessPage onBack={handleBackToDashboard} currentUser={user} />;
      }
      // Per-user page restriction: block a denied top-level module (e.g. reached
      // via an old deep link). Enforcement is also server-side; this is the UX.
      if (access.isPageDenied(currentModule)) {
        return <NoAccessPanel onBack={handleBackToDashboard} />;
      }
      if (currentModule === '__documentation') {
        return <DocumentationPage onBack={handleBackToDashboard} />;
      }
      if (currentModule === '__support') {
        return <SupportPage onBack={handleBackToDashboard} />;
      }
      if (currentModule === '__bugtracker') {
        return <BugTrackerPage onBack={handleBackToDashboard} currentUser={user} />;
      }
      if (isProductsModule) {
        return <ProductsPage onBack={handleBackToDashboard} />;
      }
      if (isProjectsModule) {
        return (
          <ProjectsPage
            onBack={handleBackToDashboard}
            currentUser={user}
            deniedTabs={new Set([...access.deniedPages].filter((k) => k.startsWith('project:')).map((k) => k.slice('project:'.length)))}
            initialProjectId={pendingRecord?.module === 'Project' ? pendingRecord.id : null}
            onInitialConsumed={consumePendingRecord}
          />
        );
      }
      if (currentModule === 'jcrm_Accounting') {
        return <AccountingReports onBack={handleBackToDashboard} />;
      }
      if (currentModule === 'Tasks') {
        return <TasksPage onBack={handleBackToDashboard} currentUser={user} />;
      }
      return (
        <ModulePage
          moduleName={currentModule}
          onBack={handleBackToDashboard}
          currentUser={user}
          initialRecordId={pendingRecord?.module === currentModule ? pendingRecord.id : null}
          onInitialConsumed={consumePendingRecord}
        />
      );
    };

    return (
      <div className="App with-sidebar">
        <Sidebar
          onModuleSelect={handleModuleSelect}
          currentModule={currentModule}
          userId={user?.id}
          deniedPages={access.deniedPages}
        />
        {renderModuleContent()}
        <CalendarToggleButton />
        <ChatToggleButton />
        <CalendarPanel />
        <ChatPanel />
        <BugReportButton user={user} currentPage={currentModule} onOpenTracker={() => setCurrentModule('__bugtracker')} />
      </div>
    );
  }

  // Show dashboard when authenticated
  return (
    <div className="App with-sidebar">
      <Sidebar
        onModuleSelect={handleModuleSelect}
        currentModule={currentModule}
        userId={user?.id}
        deniedPages={access.deniedPages}
      />

      <Dashboard
        user={user}
        onNavigate={(moduleName) => setCurrentModule(moduleName)}
        onOpenRecord={openRecord}
        headerRight={
          <>
            <HelpMenu
              onSelectHelpCenter={() => setShowHelp(true)}
              onSelectAbout={() => setShowAboutModal(true)}
            />
            <UserMenu
              user={user}
              onLogout={onLogout}
              onOpenSettings={() => setShowAccountSettings(true)}
              onOpenUserAccess={() => setCurrentModule('__user_access')}
            />
          </>
        }
      />

      <CalendarToggleButton />
      <ChatToggleButton />
      <CalendarPanel />
      <ChatPanel />
      <BugReportButton user={user} currentPage="Dashboard" onOpenTracker={() => setCurrentModule('__bugtracker')} />

      {showAccountSettings && (
        <AccountSettingsModal
          user={user}
          onClose={() => setShowAccountSettings(false)}
          onUserUpdate={onUserUpdate}
        />
      )}

      {showHelp && (
        <HelpCenter isAdmin={!!user?.isAdmin} onClose={() => setShowHelp(false)} />
      )}

      {showAboutModal && (
        <AboutModal onClose={() => setShowAboutModal(false)} />
      )}
    </div>
  );
};

// Single-session enforcement heartbeat.
// Polls session-check.php every SESSION_HEARTBEAT_MS while a user is signed in
// AND immediately when the tab regains focus. On a revoked / unknown session
// the caller is booted back to login with a "signed in elsewhere" notice.
const SESSION_HEARTBEAT_MS = 30_000;

async function checkSession(userId, sessionToken) {
  if (!userId || !sessionToken) return { active: false, reason: 'unknown' };
  try {
    const r = await fetch('/api/chat/session-check.php', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ user_id: userId, session_token: sessionToken }),
    });
    const data = await r.json().catch(() => ({}));
    if (!r.ok || !data.success) return { active: true, reason: null }; // network hiccup — don't kick
    return { active: !!data.active, reason: data.reason || null };
  } catch {
    // Offline / network — don't kick; we'll retry next heartbeat.
    return { active: true, reason: null };
  }
}

function App() {
  const [user, setUser] = useState(null);

  // Check if user is already logged in (from sessionStorage)
  useEffect(() => {
    const storedUser = sessionStorage.getItem('user');
    if (storedUser) {
      try {
        setUser(JSON.parse(storedUser));
      } catch (e) {
        sessionStorage.removeItem('user');
      }
    }
  }, []);

  const handleLoginSuccess = (userData) => {
    setUser(userData);
  };

  const handleLogout = () => {
    sessionStorage.removeItem('user');
    sessionStorage.removeItem('sessionToken');
    setUser(null);
  };

  const handleUserUpdate = (updatedUser) => {
    setUser(updatedUser);
  };

  // Session heartbeat — polls the backend to catch remote sign-outs.
  useEffect(() => {
    if (!user) return undefined;
    const token = sessionStorage.getItem('sessionToken');
    if (!token) return undefined; // Older session pre-single-session — nothing to check.

    let cancelled = false;
    const kick = (reason) => {
      if (cancelled) return;
      try {
        sessionStorage.setItem('signedOutReason', reason || 'unknown');
        sessionStorage.removeItem('user');
        sessionStorage.removeItem('sessionToken');
      } catch { /* ignore */ }
      setUser(null);
    };

    const tick = async () => {
      const { active, reason } = await checkSession(user.id, token);
      if (!active) kick(reason);
    };

    // Fire immediately + on interval + on tab visibility.
    tick();
    const iv = setInterval(tick, SESSION_HEARTBEAT_MS);
    const onVisible = () => { if (document.visibilityState === 'visible') tick(); };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      cancelled = true;
      clearInterval(iv);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [user]);

  // Show login page if not authenticated
  if (!user) {
    return <LoginPage onLoginSuccess={handleLoginSuccess} />;
  }

  // Wrap authenticated content with providers
  return (
    <CalendarProvider currentUser={user}>
      <ChatProvider currentUser={user}>
        <SubscriptionProvider currentUser={user}>
          <SubscriptionGatedContent>
            <AccessProvider user={user}>
              <FeaturesProvider>
                <AppContent user={user} onLogout={handleLogout} onUserUpdate={handleUserUpdate} />
              </FeaturesProvider>
            </AccessProvider>
          </SubscriptionGatedContent>
          <PaywallOverlay user={user} />
        </SubscriptionProvider>
      </ChatProvider>
    </CalendarProvider>
  );
}

export default App;
